import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Grid, Typography, Button } from '@mui/material';

import Language from '../Language/Language';
import Panel from '../../Elements/Panel';
import { getPostBySlug } from '../../content/blog';
import { COLOR_SECONDARY, COLOR_TEXT_MUTED } from '../../constants/styleValues';

const componentContent = {
    language_DE: {
        notFoundTitle: 'Artikel nicht gefunden',
        notFoundText: 'Diesen Blogartikel gibt es nicht (mehr).',
        backBlog: 'Zurück zum Blog',
        backHome: 'Zurück zur Startseite'
    },
    language_EN: {
        notFoundTitle: 'Article not found',
        notFoundText: 'This blog post doesn\'t exist (anymore).',
        backBlog: 'Back to blog',
        backHome: 'Back to homepage'
    },
    language_FR: {
        notFoundTitle: 'Article introuvable',
        notFoundText: 'Cet article de blog n\'existe pas (ou plus).',
        backBlog: 'Retour au blog',
        backHome: 'Retour à l\'accueil'
    }
}

//renders the post's simple content block array ({type:'p'|'h2', text} or {type:'list', items})
const renderBlock = (block, index) => {
    if(block.type === 'h2'){
        return <Typography key={index} variant="h2" sx={{mt: 4, mb: 1.5}}>{block.text}</Typography>;
    }
    if(block.type === 'list'){
        return (
            <Grid key={index} container component="ul" sx={{listStyle: 'none', pl: 0, mb: 2}}>
                {block.items.map((item, itemIndex) => (
                    <Grid item xs={12} component="li" key={itemIndex} sx={{display: 'flex', gap: '10px', mb: 1.5, alignItems: 'flex-start'}}>
                        <Typography sx={{color: COLOR_SECONDARY, lineHeight: '1.6'}}>•</Typography>
                        <Typography variant="body1" sx={{color: COLOR_TEXT_MUTED, textAlign: 'left'}}>{item}</Typography>
                    </Grid>
                ))}
            </Grid>
        );
    }
    return <Typography key={index} variant="body1" sx={{color: COLOR_TEXT_MUTED, textAlign: 'left', mb: 2, lineHeight: '1.7'}}>{block.text}</Typography>;
}

const BlogPost = () => {
    const { slug } = useParams();
    const selectedLanguage = useSelector((state) => state.Language);
    const content = componentContent[selectedLanguage];

    const post = getPostBySlug(slug);

    if(!post){
        return (
            <div>
                <Grid container justifyContent="center" textAlign="center" sx={{px: {xs: 2, sm: 3}, pt: 3}}>
                    <Language />
                </Grid>
                <Grid container justifyContent="center" textAlign="center" sx={{px: {xs: 2, sm: 3}, pt: 6}}>
                    <Grid item xs={12}>
                        <Typography variant="h2" sx={{mb: 2}}>{content.notFoundTitle}</Typography>
                        <Typography variant="body1" sx={{color: COLOR_TEXT_MUTED, mb: 3}}>{content.notFoundText}</Typography>
                        <Button variant="contained" href="/blog">{content.backBlog}</Button>
                    </Grid>
                </Grid>
            </div>
        );
    }

    const postContent = post.translations[selectedLanguage] || post.translations.language_EN;
    const canonicalUrl = `https://multiplayer-quiz.com/blog/${post.slug}`;

    return (
        <div>
            <Helmet>
                <title>{postContent.title} - Multiplayer Quiz Blog</title>
                <meta name="description" content={postContent.excerpt} />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'BlogPosting',
                        headline: post.translations.language_EN.title,
                        description: post.translations.language_EN.excerpt,
                        datePublished: post.publishedDate,
                        url: canonicalUrl,
                        author: { '@type': 'Organization', name: 'Multiplayer Quiz' }
                    })}
                </script>
            </Helmet>

            <Grid container justifyContent="center" textAlign="center" sx={{px: {xs: 2, sm: 3}, pt: 3}}>
                <Language />
            </Grid>

            <Grid container justifyContent="center" textAlign="center" sx={{px: {xs: 2, sm: 3}, pb: 6}}>
                <Grid container maxWidth="md" justifyContent="center" textAlign="center" spacing={1}>

                    <Grid item xs={12} sx={{mt: 2, mb: 0.5}}>
                        <Typography variant="caption" sx={{
                            color: COLOR_SECONDARY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em'
                        }}>
                            {postContent.categoryLabel}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{mb: 3}}>
                        <Typography variant="h1">{postContent.title}</Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Panel sx={{p: {xs: 3, sm: 4}, textAlign: 'left'}}>
                            {postContent.body.map((block, index) => renderBlock(block, index))}
                        </Panel>
                    </Grid>

                    <Grid item xs={12} sx={{mt: 4}}>
                        <Button variant='text' href="/blog" sx={{color: COLOR_TEXT_MUTED, mr: 2}}>{content.backBlog}</Button>
                        <Button variant='text' href="/" sx={{color: COLOR_TEXT_MUTED}}>{content.backHome}</Button>
                    </Grid>

                </Grid>
            </Grid>
        </div>
    );
}

export default BlogPost;
